import React, { useState } from 'react';
import '../pages/dashboard/Dashboard.css'; // Import your styles
import '@fortawesome/fontawesome-free/css/all.min.css';

// Interfaces for Campaign and DailyMetric
interface Campaign {
  id: number;
  campaign_name: string;
  ad_format: string;
  geo: string;
  traffic_source_type: string;
  bid: string;
  daily_cap: string;
  cost: string;
  impressions: number;
  clicks: number;
  conversions: number;
  win_rate: string;
  video_impressions: number;
}

interface DailyMetric {
  id: number;
  campaign_id: number;
  date: string;
  visitors: number;
  bid_requests: number;
  cost: string;
  cpc: string; // Already calculated CPC from the API
}

const AdminPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [dailyMetrics, setDailyMetrics] = useState<DailyMetric[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [noData, setNoData] = useState<boolean>(false);

  // Fetch data from the server
  const fetchData = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates.');
      return;
    }

    try {
      const response = await fetch(
        `https://bidvertiser-api.vercel.app/api/alldata/allDataTime?startDate=${startDate}&endDate=${endDate}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      if (data.campaigns.length === 0 && data.dailyMetrics.length === 0) {
        setNoData(true);
      } else {
        setNoData(false);
      }

      setCampaigns(data.campaigns);
      setDailyMetrics(data.dailyMetrics);
    } catch (error) {
      console.error('Error fetching data:', error);
      setNoData(true);
    }
  };

  const clearData = () => {
    setCampaigns([]);
    setDailyMetrics([]);
    setNoData(false);
  };

  // Aggregate metrics for each campaign (no need to recalculate CPC)
  const aggregateMetrics = (campaign: Campaign) => {
    const metrics = dailyMetrics.filter((m) => m.campaign_id === campaign.id);

    // Aggregate values
    const totalVisitors = metrics.reduce((sum, metric) => sum + metric.visitors, 0);
    const totalBidRequests = metrics.reduce((sum, metric) => sum + metric.bid_requests, 0);
    const totalCost = metrics.reduce((sum, metric) => sum + parseFloat(metric.cost), 0);

    // If there are no daily metrics, fall back to campaign cost
    const finalCost = metrics.length > 0 ? totalCost : parseFloat(campaign.cost);

    // Use the CPC directly from the API data (assume the first entry for simplicity)
    const averageCPC = metrics.length > 0 ? metrics[0].cpc : '0.00';  // Use the first metric's CPC value

    return {
      totalVisitors,
      totalBidRequests,
      totalCost: finalCost,
      averageCPC,  // Directly using the CPC from the API
    };
  };

  return (
    <div className="dashboard">
      {/* Date Range Selection */}
      <div className="date-range">
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        <button onClick={fetchData}>Generate</button>
        <button onClick={clearData}>Clear</button>
      </div>

      {/* Display Data */}
      {noData ? (
        <div>No data available for the selected date range.</div>
      ) : (
        <table className="campaign-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Campaign Name</th>
              <th>Ad Format</th>
              <th>GEO</th>
              <th>Bid</th>
              <th>Visitors</th>
              <th>Bid Requests</th>
              <th>Cost</th>
              <th>CPC</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((campaign) => {
              const { totalVisitors, totalBidRequests, totalCost, averageCPC } = aggregateMetrics(campaign);
              return (
                <tr key={campaign.id}>
                  <td>{campaign.id}</td>
                  <td>{campaign.campaign_name}</td>
                  <td>{campaign.ad_format}</td>
                  <td>{campaign.geo}</td>
                  <td>{campaign.bid}</td>
                  <td>{totalVisitors}</td>
                  <td>{totalBidRequests}</td>
                  <td>${totalCost.toFixed(2)}</td> {/* Ensure it's a number and format it */}
                  <td>{averageCPC}</td> {/* Display CPC directly from the API */}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminPage;
